import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { Ionicons } from '@expo/vector-icons';
import { imageService } from '../services/images';
import { rateLimitService } from '../services/rateLimit';
import { useDialog } from '../contexts/DialogContext';
import { Colors, ControlHeight, Fonts, Radii, Shadows } from '../constants';
import { ClubLogo } from './ClubLogo';
import { Button } from './Button';

interface ClubLogoUploaderProps {
  currentLogoUrl: string | null;
  onLogoUpdated: (publicId: string | null) => void;
  disabled?: boolean;
}

export const ClubLogoUploader: React.FC<ClubLogoUploaderProps> = ({
  currentLogoUrl,
  onLogoUpdated,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const dialog = useDialog();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handlePickImage = async () => {
    try {
      // Check rate limit
      const canUpload = await rateLimitService.checkUploadLimit();
      if (!canUpload) {
        dialog.alert(
          t('common.errors.title'),
          t('common.validation.rateLimitReached'),
          'error'
        );
        return;
      }

      // Pick single image
      const result = await imageService.pickImages(1);

      if (result.canceled || !result.assets || result.assets.length === 0) {
        return;
      }

      // Upload image
      setUploading(true);
      setUploadProgress(0);

      const uri = result.assets[0].uri;
      const publicId = await imageService.uploadSingleImage(
        uri,
        'clubswap/club-logos',
        (progress) => {
          setUploadProgress(progress);
        }
      );

      // Record upload in rate limit
      await rateLimitService.recordUpload();

      // Update logo
      onLogoUpdated(publicId);

      setUploading(false);
      setUploadProgress(0);
    } catch (err: any) {
      console.error('Logo upload error:', err);
      dialog.alert(
        t('common.errors.title'),
        err.message || t('common.validation.uploadFailed'),
        'error'
      );
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleRemoveLogo = () => {
    onLogoUpdated(null);
  };

  return (
    <View style={styles.container}>
      <Text style={styles.title}>{t('components.clubLogoUploader.title')}</Text>

      <View style={styles.content}>
        {/* Logo preview / upload progress */}
        <View style={styles.tile}>
          {uploading ? (
            <>
              <ActivityIndicator size="small" color={Colors.primary} />
              <Text style={styles.progressText}>{Math.round(uploadProgress)}%</Text>
            </>
          ) : currentLogoUrl ? (
            <ClubLogo logoUrl={currentLogoUrl} size={96} borderRadius={Radii.control} />
          ) : (
            <Ionicons name="shield-outline" size={36} color={Colors.border} />
          )}
        </View>

        {/* Action Buttons */}
        <View style={styles.buttonsContainer}>
          {!uploading && (
            <View style={styles.buttonsRow}>
              <Button
                size="sm"
                icon="cloud-upload-outline"
                title={currentLogoUrl
                  ? t('components.clubLogoUploader.changeLogo')
                  : t('components.clubLogoUploader.addLogo')}
                onPress={handlePickImage}
                disabled={disabled || uploading}
              />

              {currentLogoUrl && (
                <Pressable
                  style={({ hovered }: any) => [
                    styles.removeButton,
                    hovered && styles.removeButtonHovered,
                    disabled && styles.disabledButton,
                  ]}
                  onPress={handleRemoveLogo}
                  disabled={disabled || uploading}
                  accessibilityRole="button"
                >
                  <Text style={styles.removeButtonText}>
                    {t('components.clubLogoUploader.removeLogo')}
                  </Text>
                </Pressable>
              )}
            </View>
          )}

          {uploading && (
            <Text style={styles.uploadingText}>
              {t('components.clubLogoUploader.uploading')}
            </Text>
          )}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 16,
    padding: 16,
    backgroundColor: Colors.white,
    borderRadius: Radii.card,
    borderWidth: 1,
    borderColor: Colors.borderLight,
    ...Shadows.card,
  },
  title: {
    fontFamily: Fonts.semiBold,
    fontSize: 14,
    color: Colors.textDark,
    marginBottom: 12,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    flexWrap: 'wrap',
  },
  tile: {
    width: 96,
    height: 96,
    borderRadius: Radii.control,
    backgroundColor: Colors.background,
    borderWidth: 1,
    borderColor: Colors.borderLight,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    flexShrink: 0,
  },
  progressText: {
    fontFamily: Fonts.bold,
    fontSize: 13,
    color: Colors.accentStrong,
  },
  buttonsContainer: {
    flex: 1,
    minWidth: 180,
    gap: 8,
    alignItems: 'flex-start',
  },
  buttonsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  removeButton: {
    height: ControlHeight.sm,
    paddingHorizontal: 14,
    borderRadius: Radii.control,
    borderWidth: 1,
    borderColor: Colors.borderLight,
    backgroundColor: 'transparent',
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' && { cursor: 'pointer', transitionDuration: '150ms' } as any),
  },
  removeButtonHovered: {
    backgroundColor: Colors.background,
  },
  removeButtonText: {
    fontFamily: Fonts.semiBold,
    fontSize: 13,
    color: Colors.error,
  },
  disabledButton: {
    opacity: 0.5,
  },
  uploadingText: {
    fontFamily: Fonts.regular,
    fontSize: 12,
    color: Colors.textLight,
  },
});
